import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import {
  ThirdPartyPaymentAuthorizationDtoIn,
  ThirdPartyPaymentAuthorizationStatus,
  ThirdPartyPaymentLimitScope,
} from '@/models/in/ThirdPartyPaymentAuthorizationDtoIn';
import { ThirdPartyPaymentAuthorizationDtoOut } from '@/models/out/ThirdPartyPaymentAuthorizationDtoOut';
import { ManagementSupportedDtoIn, SupportedsQueryParams } from '@/models/in/ManagementSupportedDtoIn';
import Fund from '@/models/Fund';
import { useGetAuthApi, usePostAuthApiInfiniteQuery } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import addIcon from '@/assets/icons/add.svg';
import { israelTodayIso, parseExpiresAtFromServer } from '@/helpers/israelCalendarDate';

const QUERY_URL = 'management/third-party-payment-authorizations';
const SEARCH_DEBOUNCE_MS = 400;
const CONTACTS_PAGE_SIZE = 20;
const DEFAULT_VALIDITY_MONTHS = 12;

/**
 * Fund-wide, or one beneficiary inside a fund. Unlike the eligibility override - where the two
 * scopes are alternatives - a fund is picked either way, because an authorization never exists
 * outside one fund.
 */
type AuthorizationScope = 'fund' | 'beneficiary';

const LIMIT_SCOPES: ThirdPartyPaymentLimitScope[] = ['PerRequest', 'MonthlyCumulative', 'PeriodCumulative'];

const addMonthsIso = (months: number): string => {
  const base = new Date(`${israelTodayIso()}T00:00:00Z`);
  base.setUTCMonth(base.getUTCMonth() + months);
  return base.toISOString().slice(0, 10);
};

interface SelectedBeneficiary {
  id: number;
  label: string;
}

interface EditOrCreateAuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  authorizationToEdit?: ThirdPartyPaymentAuthorizationDtoIn | null;
  onSuccess: () => void;
}

const EditOrCreateAuthorizationDialog = ({
  open,
  onClose,
  authorizationToEdit,
  onSuccess,
}: EditOrCreateAuthorizationDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [scope, setScope] = useState<AuthorizationScope>('fund');
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<SelectedBeneficiary[]>([]);
  const [status, setStatus] = useState<ThirdPartyPaymentAuthorizationStatus>('Approved');
  const [maxAmount, setMaxAmount] = useState('');
  const [limitScope, setLimitScope] = useState<ThirdPartyPaymentLimitScope>('PerRequest');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [payeeIsraeliId, setPayeeIsraeliId] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  const [files, setFiles] = useState<(File | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [debouncedBeneficiarySearch] = useDebounceValue(beneficiarySearch, SEARCH_DEBOUNCE_MS);
  const [fundSearch, setFundSearch] = useState('');

  const isEditing = !!authorizationToEdit;

  const { data: funds, isLoading: isFundsLoading } = useGetAuthApi<Fund[]>('funds');

  const filteredFunds = useMemo(() => {
    if (!funds) return [];
    if (!fundSearch) return funds;
    return funds.filter((f) => `${f.id} ${f.name}`.toLowerCase().includes(fundSearch.toLowerCase()));
  }, [funds, fundSearch]);

  const effectiveBeneficiarySearch = debouncedBeneficiarySearch.length >= 2 ? debouncedBeneficiarySearch : undefined;
  const beneficiariesQueryParams: SupportedsQueryParams = useMemo(() => ({
    sortBy: 'ContactName',
    sortDirection: 'Asc',
    searchTerm: effectiveBeneficiarySearch,
  }), [effectiveBeneficiarySearch]);

  const {
    data: beneficiariesResponse,
    isLoading: isBeneficiariesLoading,
    isFetching: isBeneficiariesFetching,
  } = usePostAuthApiInfiniteQuery<ManagementSupportedDtoIn, SupportedsQueryParams>(
    scope === 'beneficiary' && !isEditing ? 'management/supporteds' : '',
    beneficiariesQueryParams,
    CONTACTS_PAGE_SIZE,
  );

  const pendingBeneficiarySearch = beneficiarySearch.length >= 2 ? beneficiarySearch : undefined;
  const isBeneficiarySearchStale = pendingBeneficiarySearch !== effectiveBeneficiarySearch || isBeneficiariesFetching;

  const beneficiaryResults = useMemo(
    () => beneficiariesResponse?.pages.flatMap((page) => page.data) ?? [],
    [beneficiariesResponse]
  );

  useEffect(() => {
    if (!open) return;

    if (authorizationToEdit) {
      setScope(authorizationToEdit.contactId ? 'beneficiary' : 'fund');
      setSelectedFundId(authorizationToEdit.fundId);
      setSelectedBeneficiaries(authorizationToEdit.contactId
        ? [{ id: authorizationToEdit.contactId, label: authorizationToEdit.contactName ?? '' }]
        : []);
      setStatus(authorizationToEdit.status);
      setMaxAmount(authorizationToEdit.maxAmount ? String(authorizationToEdit.maxAmount) : '');
      setLimitScope(authorizationToEdit.limitScope);
      setStartDate(parseExpiresAtFromServer(authorizationToEdit.startDate));
      setEndDate(parseExpiresAtFromServer(authorizationToEdit.endDate));
      setPayeeIsraeliId(authorizationToEdit.payeeIsraeliId ?? '');
      setDecisionReason(authorizationToEdit.decisionReason ?? '');
    } else {
      setScope('fund');
      setSelectedFundId(null);
      setSelectedBeneficiaries([]);
      setStatus('Approved');
      setMaxAmount('');
      setLimitScope('PerRequest');
      setStartDate(israelTodayIso());
      setEndDate(addMonthsIso(DEFAULT_VALIDITY_MONTHS));
      setPayeeIsraeliId('');
      setDecisionReason('');
    }
    setFiles([]);
    setBeneficiarySearch('');
    setFundSearch('');
    setError(null);
  }, [open, authorizationToEdit]);

  const toggleBeneficiary = (contact: ManagementSupportedDtoIn) => {
    setSelectedBeneficiaries((prev) => prev.some((b) => b.id === contact.id)
      ? prev.filter((b) => b.id !== contact.id)
      : [...prev, { id: contact.id, label: `${contact.firstName} ${contact.lastName} (${contact.israeliId})` }]);
    if (error) setError(null);
  };

  const buildBody = (contactId: number | null, documents?: ThirdPartyPaymentAuthorizationDtoOut['documents']): ThirdPartyPaymentAuthorizationDtoOut => ({
    fundId: selectedFundId!,
    contactId,
    payeeIsraeliId: payeeIsraeliId.trim() || null,
    status,
    // Empty means "no ceiling of our own" and is sent as 0, which the server reads the same way.
    maxAmount: maxAmount.trim() ? parseFloat(maxAmount) : 0,
    limitScope,
    startDate,
    endDate,
    decisionReason: decisionReason.trim(),
    documents,
  });

  const validate = (): string | null => {
    if (!selectedFundId) return t('thirdPartyAuthorizations.dialog.selectFundFirst');
    if (scope === 'beneficiary' && selectedBeneficiaries.length === 0) {
      return t('thirdPartyAuthorizations.dialog.selectBeneficiary');
    }
    if (maxAmount.trim()) {
      const parsed = parseFloat(maxAmount);
      if (isNaN(parsed) || parsed < 0) return t('thirdPartyAuthorizations.dialog.invalidAmount');
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      return t('thirdPartyAuthorizations.dialog.invalidDates');
    }
    // Both are yyyy-MM-dd on the Israel calendar, so a string compare is the calendar compare.
    if (endDate < startDate) return t('thirdPartyAuthorizations.dialog.endBeforeStart');
    if (payeeIsraeliId.trim() && !/^\d{5,15}$/.test(payeeIsraeliId.trim())) {
      return t('thirdPartyAuthorizations.dialog.invalidPayeeId');
    }
    // Required on an approval too - the office is recording a decision about someone else's money.
    if (!decisionReason.trim()) return t('thirdPartyAuthorizations.dialog.reasonRequired');
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const validFiles = files.filter((f): f is File => f !== null);
      const documents = validFiles.length > 0
        ? (await Promise.all(validFiles.map((file) => FileReaderHelper.fileToDocumentDtoOut(file)))).flat()
        : undefined;

      if (isEditing) {
        const contactId = scope === 'beneficiary' ? selectedBeneficiaries[0].id : null;
        await putAuth<ThirdPartyPaymentAuthorizationDtoIn, object, ThirdPartyPaymentAuthorizationDtoOut>(
          `${QUERY_URL}/${authorizationToEdit!.id}`, headers, {}, buildBody(contactId, documents)
        );
      } else {
        // A fund-wide decision is one row; a beneficiary decision is one row per person picked,
        // so the office can approve a list in a single pass.
        const targets: (number | null)[] = scope === 'fund'
          ? [null]
          : selectedBeneficiaries.map((b) => b.id);

        const failures: string[] = [];
        for (const contactId of targets) {
          try {
            await postAuth<ThirdPartyPaymentAuthorizationDtoIn, ThirdPartyPaymentAuthorizationDtoOut>(
              QUERY_URL, headers, buildBody(contactId, documents)
            );
          } catch (err: unknown) {
            const serverMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
            const label = contactId === null
              ? t('thirdPartyAuthorizations.fundScope')
              : selectedBeneficiaries.find((b) => b.id === contactId)?.label ?? String(contactId);
            failures.push(`${label}: ${serverMessage.includes('already covers')
              ? t('thirdPartyAuthorizations.dialog.duplicateError')
              : serverMessage}`);
          }
        }

        if (failures.length > 0) {
          setError(failures.join('\n'));
          // Whatever did succeed is already saved, so the table has to be refreshed even
          // though the dialog stays open on the failures.
          onSuccess();
          return;
        }
      }

      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const serverMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
      setError(serverMessage.includes('already covers')
        ? t('thirdPartyAuthorizations.dialog.duplicateError')
        : t(isEditing
          ? 'thirdPartyAuthorizations.dialog.errorUpdating'
          : 'thirdPartyAuthorizations.dialog.errorCreating'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const selectedFund = funds?.find((f) => f.id === selectedFundId);

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}
      title={isEditing
        ? t('thirdPartyAuthorizations.dialog.editTitle')
        : t('thirdPartyAuthorizations.dialog.createTitle')}
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Scope */}
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.scope')}
          </label>
          <div className="flex gap-4">
            {(['fund', 'beneficiary'] as AuthorizationScope[]).map((value) => (
              <label key={value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authorizationScope"
                  value={value}
                  checked={scope === value}
                  onChange={() => {
                    setScope(value);
                    setSelectedBeneficiaries([]);
                    setBeneficiarySearch('');
                    if (error) setError(null);
                  }}
                  disabled={isEditing}
                />
                <span className="body-sm-regular">
                  {t(value === 'fund'
                    ? 'thirdPartyAuthorizations.fundScope'
                    : 'thirdPartyAuthorizations.beneficiaryScope')}
                </span>
              </label>
            ))}
          </div>
          <p className="body-sm-regular text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.scopeHint')}
          </p>
        </div>

        {/* Fund - required for both scopes */}
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.selectFund')}
          </label>
          {isEditing ? (
            <div className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular bg-(--surface-secondary-lowest)">
              {selectedFund ? `${selectedFund.id} | ${selectedFund.name}` : authorizationToEdit?.fundName ?? selectedFundId}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <input
                type="text"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={fundSearch}
                onChange={(e) => setFundSearch(e.target.value)}
                placeholder={t('thirdPartyAuthorizations.dialog.fundSearchPlaceholder')}
              />
              <div className="border border-(--border-main) rounded-lg max-h-[180px] overflow-y-auto">
                {isFundsLoading ? (
                  <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                    {t('thirdPartyAuthorizations.dialog.loading')}
                  </div>
                ) : filteredFunds.length === 0 ? (
                  <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                    {t('thirdPartyAuthorizations.dialog.noFundsFound')}
                  </div>
                ) : (
                  filteredFunds.map((fund) => (
                    <label
                      key={fund.id}
                      className={`flex items-center gap-2 px-3 py-2 hover:bg-(--surface-secondary-lowest) cursor-pointer ${selectedFundId === fund.id ? 'bg-blue-50' : ''}`}
                    >
                      <input
                        type="radio"
                        name="authorizationFund"
                        checked={selectedFundId === fund.id}
                        onChange={() => {
                          setSelectedFundId(fund.id);
                          if (error) setError(null);
                        }}
                      />
                      <span className="body-sm-semibold">{fund.id}</span>
                      <span className="body-sm-regular">{fund.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Beneficiaries */}
        {scope === 'beneficiary' && (
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">
              {t('thirdPartyAuthorizations.dialog.selectBeneficiaryLabel')}
            </label>

            {selectedBeneficiaries.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-1">
                {selectedBeneficiaries.map((beneficiary) => (
                  <span
                    key={beneficiary.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-200 body-sm-regular shadow-sm"
                  >
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    {beneficiary.label}
                    {!isEditing && (
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500 text-lg leading-none ms-1 transition-colors"
                        onClick={() => setSelectedBeneficiaries((prev) => prev.filter((b) => b.id !== beneficiary.id))}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}

            {!isEditing && (
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  className="w-full border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={beneficiarySearch}
                  onChange={(e) => {
                    setBeneficiarySearch(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={t('thirdPartyAuthorizations.dialog.beneficiarySearchPlaceholder')}
                />
                <div className="relative border border-(--border-main) rounded-lg max-h-[180px] overflow-y-scroll">
                  {isBeneficiarySearchStale && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                      <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {isBeneficiariesLoading ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('thirdPartyAuthorizations.dialog.loading')}
                    </div>
                  ) : beneficiaryResults.length === 0 ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('thirdPartyAuthorizations.dialog.noBeneficiariesFound')}
                    </div>
                  ) : (
                    beneficiaryResults.map((contact) => (
                      <label
                        key={contact.id}
                        className={`flex items-center gap-2 px-3 py-2 hover:bg-(--surface-secondary-lowest) cursor-pointer ${selectedBeneficiaries.some((b) => b.id === contact.id) ? 'bg-blue-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBeneficiaries.some((b) => b.id === contact.id)}
                          onChange={() => toggleBeneficiary(contact)}
                        />
                        <span className="body-sm-semibold">{contact.firstName} {contact.lastName}</span>
                        <span className="text-(--font-secondary) body-sm-regular">({contact.israeliId})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Decision */}
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.decision')}
          </label>
          <div className="flex gap-4">
            {(['Approved', 'Rejected'] as ThirdPartyPaymentAuthorizationStatus[]).map((value) => (
              <label key={value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authorizationStatus"
                  value={value}
                  checked={status === value}
                  onChange={() => setStatus(value)}
                />
                <span className="body-sm-regular">
                  {t(value === 'Approved'
                    ? 'thirdPartyAuthorizations.approve'
                    : 'thirdPartyAuthorizations.reject')}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Ceiling - only meaningful on an approval */}
        {status === 'Approved' && (
          <>
            <div className="flex gap-4">
              <div className="flex flex-col gap-1 flex-1">
                <label htmlFor="authorization-max-amount" className="body-sm-semibold text-(--font-secondary)">
                  {t('thirdPartyAuthorizations.dialog.maxAmount')}
                </label>
                <input
                  id="authorization-max-amount"
                  type="number"
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={maxAmount}
                  onChange={(e) => {
                    setMaxAmount(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={t('thirdPartyAuthorizations.dialog.maxAmountPlaceholder')}
                  dir="ltr"
                  min="0"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label htmlFor="authorization-limit-scope" className="body-sm-semibold text-(--font-secondary)">
                  {t('thirdPartyAuthorizations.dialog.limitScope')}
                </label>
                <select
                  id="authorization-limit-scope"
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={limitScope}
                  onChange={(e) => setLimitScope(e.target.value as ThirdPartyPaymentLimitScope)}
                >
                  {LIMIT_SCOPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`thirdPartyAuthorizations.limitScope.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="authorization-payee-id" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyAuthorizations.dialog.payeeIsraeliId')}
              </label>
              <input
                id="authorization-payee-id"
                type="text"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={payeeIsraeliId}
                onChange={(e) => {
                  setPayeeIsraeliId(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('thirdPartyAuthorizations.dialog.payeeIsraeliIdPlaceholder')}
                dir="ltr"
              />
            </div>
          </>
        )}

        {/* Validity */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="authorization-start-date" className="body-sm-semibold text-(--font-secondary)">
              {t('thirdPartyAuthorizations.dialog.startDate')}
            </label>
            <input
              id="authorization-start-date"
              type="date"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (error) setError(null);
              }}
              dir="ltr"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="authorization-end-date" className="body-sm-semibold text-(--font-secondary)">
              {t('thirdPartyAuthorizations.dialog.endDate')}
            </label>
            <input
              id="authorization-end-date"
              type="date"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={endDate}
              min={startDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (error) setError(null);
              }}
              dir="ltr"
            />
          </div>
        </div>

        {/* Decision reason */}
        <div className="flex flex-col gap-1">
          <label htmlFor="authorization-reason" className="body-sm-semibold text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.decisionReason')}
          </label>
          <textarea
            id="authorization-reason"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
            rows={3}
            value={decisionReason}
            onChange={(e) => {
              setDecisionReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t(status === 'Rejected'
              ? 'thirdPartyAuthorizations.dialog.rejectionReasonPlaceholder'
              : 'thirdPartyAuthorizations.dialog.decisionReasonPlaceholder')}
          />
          {status === 'Rejected' && (
            <p className="body-sm-regular text-(--font-secondary)">
              {t('thirdPartyAuthorizations.dialog.rejectionVisibleToCoordinator')}
            </p>
          )}
        </div>

        {/* Attachments */}
        <div className="flex flex-col gap-2">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('thirdPartyAuthorizations.dialog.attachments')}
          </label>

          {isEditing && authorizationToEdit?.documents && authorizationToEdit.documents.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="body-sm-regular text-(--font-secondary)">
                {t('thirdPartyAuthorizations.dialog.existingFiles')}
              </span>
              <div className="flex flex-col gap-1">
                {authorizationToEdit.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <a
                      href={doc.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="body-sm-regular text-blue-600 hover:text-blue-800 underline truncate"
                    >
                      {doc.name}
                    </a>
                    <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {files.map((file, index) => (
            <FileUpload
              key={index}
              placeholder={t('thirdPartyAuthorizations.dialog.attachments')}
              value={file}
              onChange={(newFile) => {
                setFiles((prev) => {
                  const updated = [...prev];
                  updated[index] = newFile;
                  return updated;
                });
              }}
              customDescription={{
                description: file?.name ?? '',
                onDescriptionChange: () => {},
                onRemove: () => setFiles((prev) => prev.filter((_, i) => i !== index)),
              }}
            />
          ))}

          <button
            type="button"
            onClick={() => setFiles((prev) => [...prev, null])}
            className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
          >
            <img src={addIcon} alt="" />
            {t('thirdPartyAuthorizations.dialog.addFile')}
          </button>
        </div>

        {error && <p className="text-red-500 body-sm-regular whitespace-pre-line">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? t('saving')
              : !isEditing && scope === 'beneficiary' && selectedBeneficiaries.length > 1
                ? t('thirdPartyAuthorizations.dialog.saveMultiple', { count: selectedBeneficiaries.length })
                : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateAuthorizationDialog;
